/**
 * Recently Viewed Products — Web Component
 * Tracks product visits in localStorage and renders a browsing history grid.
 * Data stored per product: { id, handle, title, image, price, url, ts }
 * No network requests — renders entirely from cached data.
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABRecentlyViewed extends ABBaseComponent {
  static STORAGE_KEY = 'ab-recently-viewed';
  static MAX_ITEMS = 10;
  static TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  connectedCallback() {
    const productId = this.dataset.productId;
    const handle = this.dataset.productHandle;
    const title = this.dataset.productTitle;
    const image = this.dataset.productImage;
    const price = this.dataset.productPrice;
    const url = this.dataset.productUrl;

    if (productId && handle) {
      this.trackProduct({ id: productId, handle, title, image, price, url });
    }

    this.loadRecentlyViewed(productId);
  }

  /**
   * Add current product to browsing history in localStorage.
   */
  trackProduct(product) {
    try {
      const items = this.getItems();
      // Remove duplicate and expired entries
      const now = Date.now();
      const filtered = items.filter(
        (item) => item.id !== product.id && now - item.ts < ABRecentlyViewed.TTL_MS
      );
      // Prepend new item
      filtered.unshift({
        id: product.id,
        handle: product.handle,
        title: product.title || '',
        image: product.image || '',
        price: product.price || '',
        url: product.url || '',
        ts: now,
      });
      // Cap at max
      const capped = filtered.slice(0, ABRecentlyViewed.MAX_ITEMS);
      localStorage.setItem(ABRecentlyViewed.STORAGE_KEY, JSON.stringify(capped));
    } catch {
      // localStorage unavailable (private browsing, quota exceeded) — silently fail
    }
  }

  /**
   * Read items from localStorage, filtering expired entries.
   * @returns {Array}
   */
  getItems() {
    try {
      const raw = localStorage.getItem(ABRecentlyViewed.STORAGE_KEY);
      if (!raw) return [];
      const items = JSON.parse(raw);
      if (!Array.isArray(items)) return [];
      const now = Date.now();
      return items.filter((item) => now - item.ts < ABRecentlyViewed.TTL_MS);
    } catch {
      return [];
    }
  }

  /**
   * Render recently viewed products, excluding the current product.
   */
  loadRecentlyViewed(excludeProductId) {
    const displayCount = parseInt(this.dataset.count, 10) || 6;
    const items = this.getItems().filter((item) => String(item.id) !== String(excludeProductId));
    const toShow = items.slice(0, displayCount);

    if (toShow.length < 2) {
      this.hidden = true;
      return;
    }

    const grid = this.$('.ab-recently-viewed__grid');
    if (!grid) return;

    grid.innerHTML = toShow
      .map(
        (item) => `
      <a href="${this.escapeAttr(item.url)}" class="ab-recently-viewed__card">
        <div class="ab-recently-viewed__media">
          ${
            item.image
              ? `<img
                  src="${this.escapeAttr(item.image)}"
                  alt="${this.escapeAttr(item.title)}"
                  class="ab-recently-viewed__image"
                  loading="lazy"
                  width="300"
                  height="300"
                />`
              : `<div class="ab-recently-viewed__placeholder"></div>`
          }
        </div>
        <div class="ab-recently-viewed__info">
          <span class="ab-recently-viewed__card-title">${this.escapeHtml(item.title)}</span>
          <span class="ab-recently-viewed__price">${this.escapeHtml(item.price)}</span>
        </div>
      </a>
    `
      )
      .join('');

    // Show the component
    this.hidden = false;
  }

  /**
   * Escape HTML entities to prevent XSS from stored data.
   */
  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Escape for use in HTML attributes.
   */
  escapeAttr(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

defineComponent('ab-recently-viewed', ABRecentlyViewed);
